import { DocumentNode } from '@apollo/client';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { HTMLAttributeAnchorTarget } from 'react';

import { JobPositionLocation } from '@pflegia/data-access';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import Button, { getButtonClassName } from '@pflegia/ui/shared/button';
import LinkWithLoader from '@pflegia/ui/shared/link-with-loader/LinkWithLoader.component';

import InviteCtaButtons from '@components/position-card/InviteCtaButtons.component';
import LearnMoreButton from '@components/position-card/LearnMoreButton.component';
import SignUpButton from '@components/sign-up-dialogue/SignUpButton.component';

import { CHAT_BY_JOB_POSITION_ID } from '@shared/application/constants';
import { useJobDetails } from '@shared/context/job-details.context';
import { MagazinAttribution } from '@shared/utils/magazin';

import OffersDialogueButton from '../dialogue';

export interface PositionCardButtonsProps {
  geoLocation?: JobPositionLocation | null;
  connectionStatus: CardStatus;
  connectionType: CardType;
  isLinkLoadingDisabled?: boolean;
  jobTitle: string;
  jobId: string;
  slug?: string | null;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  shouldShowDetails: boolean;
  isReAcceptable?: boolean;
  employerName: string;
  isJobBoard?: boolean;
  slimmed?: boolean;
  refetchQueries?: DocumentNode[];
  target?: HTMLAttributeAnchorTarget;
  onChatClick?: () => void;
  handleAfterAccept?: () => void;
  positionLink: string;
  magazinAttribution?: MagazinAttribution;
}

export const PositionCardButtons: React.FC<PositionCardButtonsProps> = ({
  geoLocation,
  connectionStatus,
  connectionType,
  isLinkLoadingDisabled = false,
  jobTitle,
  jobId,
  slug,
  openDialog,
  shouldShowDetails,
  isReAcceptable,
  employerName,
  isJobBoard = false,
  slimmed = false,
  refetchQueries,
  target,
  onChatClick,
  handleAfterAccept,
  positionLink,
  magazinAttribution,
}) => {
  const { t } = useTranslation('job', { keyPrefix: 'positionCard.buttons' });
  const isAuthenticated = useAuth();

  const { setShowPausedModal, showPausedModal } = useJobDetails();

  const handleLearnMoreOnClick = isJobBoard
    ? undefined
    : () => {
        if (showPausedModal) setShowPausedModal(false);
      };

  const slimStyle = slimmed ? 'px-2.5 py-2 sm:py-2 md:px-6 xl:px-6' : '';
  if (connectionType === 'default') {
    return (
      <div className='grid w-full grid-cols-2 gap-2 sm:w-auto'>
        <LearnMoreButton
          title={t('learnMore')}
          data-testid={`learn-more-position-link-${connectionType}`}
          isLinkLoadingDisabled={isLinkLoadingDisabled}
          href={positionLink}
          target={target}
          onClick={handleLearnMoreOnClick}
          className={slimStyle}
        />
        {isAuthenticated ? (
          <OffersDialogueButton
            jobPositionId={jobId}
            label={t('apply')}
            openDialog={openDialog}
            status='pending'
            jobTitle={jobTitle}
            type='default'
            employerName={employerName}
            slimmed={slimmed}
            slug={slug}
            refetchQueries={refetchQueries}
          />
        ) : (
          <SignUpButton
            jobPositionAttributes={{ id: jobId, geoLocation, jobTitle, slug }}
            colorScheme='apply'
            className={clsx('w-full sm:w-auto', slimStyle)}
            label={t('apply')}
            magazinAttribution={magazinAttribution}
          />
        )}
      </div>
    );
  }

  if (connectionType === 'application' && connectionStatus === 'pending') {
    return (
      <div className='grid w-full grid-cols-2 gap-2 sm:w-auto sm:min-w-105 lg:min-w-87.5 xl:min-w-105'>
        <LearnMoreButton
          title={t('learnMore')}
          className='col-start-2 w-full lg:w-auto'
          data-testid={`learn-more-position-link-${connectionType}-${connectionStatus}`}
          isLinkLoadingDisabled={isLinkLoadingDisabled}
          href={positionLink}
          target={target}
          onClick={handleLearnMoreOnClick}
        />
      </div>
    );
  }
  if (connectionType === 'invitation' && connectionStatus === 'pending') {
    return (
      <InviteCtaButtons
        isLinkLoadingDisabled={isLinkLoadingDisabled}
        jobTitle={jobTitle}
        jobId={jobId}
        slug={slug}
        openDialog={openDialog}
        employerName={employerName}
        refetchQueries={refetchQueries}
        target={target}
        handleAfterAccept={handleAfterAccept}
        positionLink={positionLink}
        onLearnMoreClick={handleLearnMoreOnClick}
      />
    );
  }
  if (connectionType === 'application' && connectionStatus === 'declined') {
    return <p className='text-declined w-full text-center text-2xl sm:w-auto'>{t('applicationDeclined')}</p>;
  }
  if (connectionType === 'invitation' && connectionStatus === 'declined') {
    return (
      <>
        <p
          data-testid='offer-invitation-declined-p'
          className='text-declined mt-8 w-full text-center text-xl leading-8 sm:w-auto md:mt-0 md:text-end md:text-2xl'>
          {t('offerDeclined')}
        </p>
        {isReAcceptable && (
          <button
            onClick={() => openDialog({ status: 'reAcceptable', type: 'default', jobPositionId: jobId, jobPositionTitle: jobTitle })}
            type='button'
            className='md:text-bas text-secondary relative w-full text-center text-sm leading-5 whitespace-nowrap underline md:float-right md:w-fit md:leading-6'>
            {t('reAccept')}
          </button>
        )}
      </>
    );
  }
  if (connectionStatus === 'accepted') {
    return (
      <div className='grid w-full grid-cols-2 gap-2 sm:w-auto sm:min-w-105 lg:min-w-87.5 xl:min-w-105'>
        <div>
          {shouldShowDetails && (
            <LearnMoreButton
              title={t('learnMore')}
              data-testid={`learn-more-position-link-${connectionStatus}`}
              isLinkLoadingDisabled={isLinkLoadingDisabled}
              href={positionLink}
              target={target}
              onClick={handleLearnMoreOnClick}
              className='min-w-full'
            />
          )}
        </div>
        {onChatClick ? (
          <Button
            className={getButtonClassName('apply', 'grid place-items-center')}
            data-testid={`button-link-${connectionStatus}`}
            onClick={onChatClick}>
            {t('inbox')}
          </Button>
        ) : (
          <LinkWithLoader
            className={getButtonClassName('apply', 'grid place-items-center')}
            data-testid={`button-link-${connectionStatus}`}
            href={CHAT_BY_JOB_POSITION_ID(jobId)}>
            {t('inbox')}
          </LinkWithLoader>
        )}
      </div>
    );
  }

  return (
    <LearnMoreButton
      title={t('learnMore')}
      data-testid='learn-more-position-link'
      isLinkLoadingDisabled={isLinkLoadingDisabled}
      href={positionLink}
      target={target}
      className='w-full sm:w-auto'
      onClick={handleLearnMoreOnClick}
    />
  );
};
